"use client";

import React, { useState } from 'react';
import { Check, X, Loader2, Gift, Settings2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { creditPacks, pricingPlans } from "@/features/sonas/mockData";
import { createCharge } from "@/services/sellapp";
import { useAuth } from "@/integrations/supabase/AuthProvider";
import { useSonas } from "../SonasContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import GiftProDialog from "./GiftProDialog";

const PricingDialog = ({ onOpenChange, open }) => {
  const { user, profile, refreshProfile } = useAuth();
  const [loadingId, setLoadingId] = useState(null);
  const [isGiftingOpen, setIsGiftingOpen] = useState(false);

  const isPro = profile?.is_pro === true;

  const handlePurchase = async (item) => {
    if (!user) {
      toast.error("Please log in to make a purchase.");
      return;
    }

    if (item.id === 'pro' && isPro) {
      toast.info("Architect node is already at Pro level.");
      return;
    }

    setLoadingId(item.id);
    try {
      const priceValue = parseFloat(item.price.replace(/[£$]/g, ''));
      const totalCents = Math.round(priceValue * 100);

      const charge = await createCharge({
        email: user.email,
        total: totalCents,
        currency: "USD",
        description: `Sonas Chat: ${item.label || item.credits}`,
        reference: `user_${user.id}_${item.id}_${Date.now()}`
      });

      const checkoutUrl = charge.url || charge.data?.url;

      if (checkoutUrl) {
        toast.success("Redirecting to secure payment processor...");
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      toast.error(err.message || "Payment initialization failed.");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent
          className="glass-surface max-h-[90vh] max-w-4xl overflow-y-auto rounded-3xl border-sonas-border bg-sonas-bg p-8 shadow-2xl"
        >
          <DialogHeader className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="text-sonas-accent h-5 w-5" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-sonas-accent">Secure Transaction Layer</span>
            </div>
            <DialogTitle className="text-3xl font-syne font-bold">
              Store Purchases
            </DialogTitle>
            <DialogDescription className="text-sonas-muted text-sm mt-2">
              Compare plans and pick a credit pack that fits your current sprint. Credits are applied securely after payment verification.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 md:grid-cols-2">
            {pricingPlans.map((plan) => {
              const isActivePro = plan.id === 'pro' && isPro;
              
              return (
                <article
                  className={cn(
                    "rounded-[32px] border p-8 flex flex-col gap-6 transition-all",
                    plan.id === 'pro' 
                      ? "border-sonas-accent/30 bg-sonas-accent/5" 
                      : "border-sonas-border bg-white/[0.02]"
                  )}
                  key={plan.id}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sonas-dim">
                      {plan.label}
                    </p>
                    <span className="rounded-full bg-sonas-accent/10 px-3 py-1 text-[10px] font-bold uppercase text-sonas-accent">
                      {isActivePro ? "Active Node" : plan.badge}
                    </span>
                  </div>

                  <div className="space-y-1">
                    <p className="text-4xl font-bold">
                      {plan.price}
                      <span className="ml-1 text-sm font-medium text-sonas-dim italic">/{plan.period}</span>
                    </p>
                    <p className="text-xs text-sonas-muted leading-relaxed">
                      {plan.description}
                    </p>
                  </div>

                  {isActivePro ? (
                    <div className="flex gap-3">
                      <Button 
                        onClick={() => window.open('https://sell.app/customer', '_blank')}
                        className="flex-1 h-12 rounded-2xl bg-white/5 border border-white/10 text-xs font-bold gap-2"
                      >
                        <Settings2 size={14} /> Manage
                      </Button>
                      <Button 
                        onClick={() => setIsGiftingOpen(true)}
                        className="flex-1 h-12 rounded-2xl bg-sonas-accent/10 border border-sonas-accent/20 text-sonas-accent text-xs font-bold gap-2"
                      >
                        <Gift size={14} /> Gift Pro
                      </Button>
                    </div>
                  ) : (
                    <Button
                      className={cn(
                        "h-12 w-full rounded-2xl text-sm font-bold shadow-lg transition-all active:scale-[0.98]",
                        plan.id === 'pro'
                          ? "bg-sonas-accent text-sonas-bg hover:opacity-90 shadow-sonas-accent/20"
                          : "bg-white/10 text-sonas-text border border-white/5 hover:bg-white/15 cursor-default"
                      )}
                      disabled={plan.ctaDisabled || loadingId === plan.id}
                      onClick={() => plan.id !== 'free' && handlePurchase(plan)}
                    >
                      {loadingId === plan.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {plan.cta}
                    </Button>
                  )}

                  <div className="space-y-3 pt-4 border-t border-white/5">
                    {plan.features.map((feature, index) => (
                      <div className="flex items-center gap-2.5 text-xs font-medium" key={index}>
                        <Check className="h-3.5 w-3.5 text-sonas-accent" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>

          <section className="mt-12 space-y-6">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-bold uppercase tracking-widest text-sonas-dim">
                Credit Packs
              </h3>
              <div className="h-[1px] flex-1 bg-white/5" />
            </div>
            
            <div className="grid gap-4 sm:grid-cols-3">
              {creditPacks.map((pack) => (
                <article
                  className="rounded-2xl border border-sonas-border bg-white/[0.02] p-6 hover:border-sonas-accent/30 transition-all group"
                  key={pack.id}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim group-hover:text-sonas-accent transition-colors">
                      {pack.label}
                    </p>
                    {pack.badge && (
                      <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[8px] font-bold uppercase text-amber-500">
                        {pack.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-2xl font-bold">{pack.price}</p>
                  <p className="text-[10px] font-mono text-sonas-muted mt-1">{pack.credits}</p>

                  <Button
                    className="mt-6 h-10 w-full rounded-xl bg-sonas-text text-sonas-bg font-bold text-xs hover:opacity-90 active:scale-[0.98] transition-all"
                    disabled={loadingId === pack.id}
                    onClick={() => handlePurchase(pack)}
                  >
                    {loadingId === pack.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : "Purchase Pack"}
                  </Button>
                </article>
              ))}
            </div>
          </section>
        </DialogContent>
      </Dialog>

      <GiftProDialog open={isGiftingOpen} onOpenChange={setIsGiftingOpen} />
    </>
  );
};

export default PricingDialog;